import "./App.css";
import { useState, useEffect } from "react";
import { Auth0Provider } from "@auth0/auth0-react";
import { useAuth0 } from "@auth0/auth0-react";
import NavBar from "./components/NavBar";
import { MenuItem, FormControl, InputLabel, Select, Card, CardContent } from "@mui/material";
import LanguageDropdown from "./components/LanguageDropdown";
import LevelDropdown from "./components/LevelDropdown"; 
import Dictionary from "./components/Dictionary";
import ArticleFeed from "./components/ArticleFeed";
import WordsPreview from "./components/WordsPreview";


import {
  getWords,
  getTopics,
  addWord,
  incrementWord,
  deleteWord,
  addTopic,
  deleteTopic,
} from "./services/backend";
import {
  INITIAL_WORDS,
  INITIAL_TOPICS,
  INITIAL_ARTICLES,
} from "./default_data";

import Grid from "@mui/material/Grid";
import Container from "@mui/material/Container";
import { Typography } from "@mui/material";

function AppContent() {
  const { isAuthenticated, user, isLoading } = useAuth0();

  const [words, setWords] = useState(INITIAL_WORDS);
  const [wordsInitialized, setWordsInitialized] = useState(false);

  const [topics, setTopics] = useState(INITIAL_TOPICS);
  const [topicsInitialized, setTopicsInitialized] = useState(false);

  const [globalError, setGlobalError] = useState();


  const [selectedWord, setSelectedWord] = useState(null);
  const [level, setLevel] = useState(() => {
    return localStorage.getItem('level') || "C2";
  });

  const [language, setLanguage] = useState(() => {
    return localStorage.getItem('language') || "en";
  });

  useEffect(() => {
    localStorage.setItem('level', level);
  }, [level]);
  
  useEffect(() => {
    localStorage.setItem('language', language);
  }, [language]);
  
  
  const addWordHandler = async (word, user_id) => {
    console.log("Adding word: " + word);
    if (!words.map((w) => w.name).includes(word)) {
      try {
        const object = await addWord(word, user_id);
        const id = object.data.id;
        console.log("Added word with id: " + id);
        setWords((prevState) => [
          { id: id, name: word, count: 0 },
          ...prevState,
        ]);
      } catch (error) {
        setGlobalError(error.message);
      }
    } else {
      const updatedWord = words.find((w) => w.name === word);
      updatedWord.count++;
      setWords((prevState) => {
        const filteredWords = prevState.filter((w) => w.id !== updatedWord.id);
        return [updatedWord, ...filteredWords];
      });
      incrementWord(updatedWord.id);
    }
  };

  const addTopicHandler = async (topic, user_id) => {
    try {
      const object = await addTopic(topic, user_id);
      const id = object.data.id;
      setTopics((prevState) => [{ id: id, name: topic }, ...prevState]);
    } catch (error) {
      setGlobalError(error.message);
    }
  };

  const deleteWordHandler = async (wordId) => {
    try {
      const wordToDelete = words.find((w) => w.id === wordId);
      if (wordToDelete) {
        const newWords = words.filter((w) => w.id !== wordId);
        setWords(newWords);
        await deleteWord(wordId, user.sub, wordToDelete.name);
      }
    } catch (error) {
      setGlobalError(error.message);
    }
  };

  const deleteTopicHandler = async (topicId) => {
    console.log("Deleting topic: " + topicId);
    try {
      const topicToDelete = topics.find((t) => t.id === topicId);
      if (topicToDelete) {
        const newTopics = topics.filter((t) => t.id !== topicId);
        setTopics(newTopics);
        await deleteTopic(topicId, user.sub, topicToDelete.name);
      }
    } catch (error) {
      setGlobalError(error.message);
    }
  };

  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      getTopics(user.sub)
        .then((response) => {
          if (response && response.data) {
            console.log(`Retrieved ${response.data.length} topics from backend: ${response.data.slice(0,5).map(t => t.name).join(", ")}...`);
            setTopics(response.data);
            setTopicsInitialized(true);
          }
        })
        .catch((error) => {
          console.error("Error fetching topics:", error);
        });
    }
  }, [user, isLoading, isAuthenticated]);

  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      getWords(user.sub)
        .then((response) => {
          if (response && response.data) {
            console.log(`Retrieved ${response.data.length} words from backend: ${response.data.slice(0,5).map(w => w.name).join(", ")}...`);
            setWords(response.data);
            setWordsInitialized(true);
          }
        })
        .catch((error) => {
          console.error("Error fetching words:", error);
        });
    }
  }, [user, isLoading, isAuthenticated]);

  if (isLoading) {
    return (
      <Container maxWidth="xl" disableGutters>
        <Typography variant="h5" textAlign="center" sx={{ mt: 4 }}>
          Loading LexiNews...
        </Typography>
      </Container>
    );
  }

  const renderArticleFeed = wordsInitialized && topicsInitialized && language !== null && level !== null;
  
  return (
    <>
      <NavBar />
      <Container maxWidth="xl" disableGutters>
        {isAuthenticated && !isLoading ? (
          <Grid container spacing={2}>
            <Grid item xs={12} md={12} lg={6} order={{ xs: 1, lg: 2 }}>
              {globalError && (
                <Typography
                  textAlign="center"
                  variant="overline"
                  display="block"
                  gutterBottom
                >
                  {globalError}
                </Typography>
              )}
              <Card>
                <CardContent>
                  <LanguageDropdown
                    language={language}
                    setLanguage={setLanguage}
                  />
                  <LevelDropdown
                    level={level}
                    setLevel={setLevel}
                  />
                  {renderArticleFeed? (<ArticleFeed
                    language={language}
                    level={level}
                    keywords={topics}
                    words={words}
                    onAdd={addWordHandler}
                    user_id={user.sub}
                    setSelectedWord={setSelectedWord}
                  />) : (
                    <p>Preparing your feed...</p>
                  )}
                </CardContent>

                <Dictionary
                  word={selectedWord}
                  user_id={user.sub}
                  language={language}
                  onClose={() => setSelectedWord(null)}
                />
              </Card>
            </Grid>
            <Grid item xs={12} md={6} lg={3} order={{ xs: 2, lg: 1 }}>
              <WordsPreview
                title="Topics"
                words={topics}
                onDelete={deleteTopicHandler}
                onAdd={addTopicHandler}
                user_id={user.sub}
              />
            </Grid>
            <Grid item xs={12} md={6} lg={3} order={{ xs: 3, lg: 3 }}>
              <WordsPreview
                title="Words"
                words={words}
                onDelete={deleteWordHandler}
                onAdd={addWordHandler}
                user_id={user.sub}
              />
            </Grid>
          </Grid>
        ) : (
          <Typography variant="h5" textAlign="center" sx={{ mt: 4 }}>
            Please log in to access LexiNews
          </Typography>
        )}
      </Container>
    </>
  );
}

function App() {
  return (
    <Auth0Provider
      domain={process.env.REACT_APP_AUTH0_DOMAIN}
      clientId={process.env.REACT_APP_AUTH0_CLIENT_ID}
      audience={process.env.REACT_APP_AUTH0_AUDIENCE}
      authorizationParams={{
        redirect_uri: window.location.origin,
      }}
      cacheLocation="localstorage"
      useRefreshTokens={true}
    >
      <AppContent />
    </Auth0Provider>
  );
}

export default App;
